import { forwardRef, useEffect, useRef } from "react";

// A textarea that grows to fit its content, up to `maxRows` and then scrolls. Sized from `value`, so text that
// arrives without typing (a pick, a dictation, a reset) is sized too.
const AutoResizeTextarea = forwardRef(
  (
    { value, onChange, minRows = 1, maxRows, style, onInput, ...rest },
    forwardedRef,
  ) => {
    const innerRef = useRef(null);

    const setRefs = (el) => {
      innerRef.current = el;
      if (typeof forwardedRef === "function") forwardedRef(el);
      else if (forwardedRef) forwardedRef.current = el;
    };

    const resize = () => {
      const el = innerRef.current;
      if (!el) return;

      // Collapse first so the box can shrink as well as grow — scrollHeight
      // never reports less than the current height.
      el.style.height = "auto";

      if (!maxRows) {
        el.style.height = `${el.scrollHeight}px`;
        el.style.overflowY = "hidden";
        return;
      }

      const lineHeight =
        parseInt(window.getComputedStyle(el).lineHeight, 10) || 26.8;
      const maxHeight = lineHeight * maxRows;
      const overflows = el.scrollHeight > maxHeight;

      el.style.height = `${overflows ? maxHeight : el.scrollHeight}px`;
      el.style.overflowY = overflows ? "auto" : "hidden";
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(resize, [value, maxRows]);

    const handleInput = (e) => {
      resize();
      onInput?.(e);
    };

    return (
      <textarea
        ref={setRefs}
        value={value}
        onChange={onChange}
        onInput={handleInput}
        rows={minRows}
        style={{ resize: "none", ...style }}
        {...rest}
      />
    );
  },
);

AutoResizeTextarea.displayName = "AutoResizeTextarea";

export default AutoResizeTextarea;
